import '../../../../../css/index.css';
import Layout from '../Layout/Layout';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardApi, ServerApi } from '@yoso/analytics-portal-client';
import StoryItem from './StoryItem';
import { useReports } from '../../../../../common/contexts/Reports';
import { useAuthentication } from '../../../../../common/contexts/Authentication';
import { useParams } from 'react-router-dom';

const gridStyle = Object.freeze({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  maxWidth: '100%',
  minWidth: '100%',
  justifyContent: 'space-between',
});

export function createUrl(obj, servers) {
  const server = servers?.find((server) => server.name === obj.server);
  return server ? server.url + obj.path : '';
};


export function fillParams(input, variables) {
  const { source: { request } } = input;

  let url = request.path;
  // Replace variables in the path
  request.parameters.forEach(parameter => {
    if (parameter.location === 'path') {
      const variable = variables.find(v => v.name === parameter.variable);
      if (variable) {
        url = url.replace(`{${parameter.name}}`, variable.value);
      }
    }
  });

  // Add query parameters to the url
  const queryParams = request.parameters
    .filter(parameter => parameter.location === 'query')
    .map(parameter => {
      const variable = variables.find(v => v.name === parameter.variable);
      return variable && variable.value !== null ? `${parameter.name}=${variable.value}` : '';
    })
    .filter(query => query !== '')
    .join('&');

  url += queryParams === '' ? '' : (url.includes('?') ? '&' : '?') + queryParams;

  return {
    ...input,
      source: {
        ...input.source,
        request: {
          ...input.source.request,
          path: url,
        },
      },
    };
};

const createWidget = (name, servers, dashboard, variables) =>

dashboard.widgets
  .filter(widget => widget.name === name)
  .map(widget => {
    if(!widget.data && !widget.options && !widget.source && !widget.value) return widget;
    if(widget.source){
      widget.data= {
        source:{
          request:{
            ...widget.source,
          },
        },
      };
    }

    const source = widget.data || widget.options || widget.value;
    const newSource = fillParams(source, variables);
    const url = createUrl(newSource.source.request, servers);
    return {
      ...widget,
      data: {
        ...source,
        source: {
          ...source.source,
          request: {
            ...source.source.request,
            url,
          },
        },
      },
    };
  });

const isVoucherRelated = (w, voucher) => {
  const params = w.data?.source?.request?.parameters || w.data?.options?.request?.parameters;
  if(params && params.find(p => p.name === 'voucher-id')){
    return voucher;
  }
  return null;
};

const isDateRelated = (w, startDate, endDate) => {
  const params = w.data?.source?.request?.parameters || w.data?.options?.request?.parameters;

  if((params && params.find(p => p.name === 'date-start' || p.name === 'date-end')) || w.widget === 'DatetimeRangeWidget'){
    return { startDate, endDate };
  }
  return { startDate: null, endDate: null };
};

function General(){
  const { getMerchantUuid } = useAuthentication();
  const { currentDashboard, variables, setVariables, servers, setServers } = useReports();
  const [ voucher, setVoucher ] = useState({y:'', x:''});
  const [ startDate, setStartDate ] = useState(new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().slice(0, 10));
  const [ endDate, setEndDate ] = useState(new Date().toISOString().slice(0, 10));
  const [ dashboard, setDashboard ] = useState(null);
  const [ widgets, setWidgets ] = useState([]);
  const [ mappingData, setMappingData ] = useState(true);
  const [ usingVoucher, setUsingVoucher ] = useState(false);
  const [ usingDate, setUsingDate ] = useState(false);
  const [ firstRender, setFirstRender ] = useState(true);
  const params = useParams();

  useEffect(() => {
    if(currentDashboard){
      const dashboardApi = new DashboardApi();
      const serverApi = new ServerApi();

      const fetchServerData = async () => {
        const res = await serverApi.listServers();
        setServers(res.data._embedded.servers);
      };
      const fetchDashboardData = async () => {
        const name = params.name ? params.name : currentDashboard.name;
        const res = await dashboardApi.getDashboard('etuisa', name);
        setDashboard(res.data);
      };

      if (currentDashboard) {
        fetchDashboardData();
        fetchServerData();
      }
    }
  }, [ currentDashboard, params.name ]);

  const fillVariables = useCallback((variable) => {
    const { name } = variable;
    const obj = { name };

    switch(name){
      case 'merchant_uuid':
        obj.value = getMerchantUuid();
        break;
      case 'voucher_id':
        if(voucher){
          obj.value = voucher.x;
        }
        break;
      case 'date_start':
        obj.value = startDate;
        break;
      case 'date_end':
        obj.value = endDate;
        break;
      default:
        obj.value = '';
        break;
    };
    return obj;
  }, [ getMerchantUuid, voucher, startDate, endDate ]);

  const fetchVoucherUuid = async () => {
    const variables = dashboard.variables.map(variable => fillVariables(variable));
    const listSelectWidget = dashboard.widgets.find(widget => widget.widget === 'ListSelectWidget');
    const request = listSelectWidget.options.source.request;
    const url = `${createUrl(request, servers)}?merchant-uuid=${getMerchantUuid()}`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
      }
      const voucherData = await response.json();
      const voucherObj = {
        x: voucherData.options.additional[0].value ?? null,
        y: voucherData.options.additional[0].title,
      };
      await setVoucher(voucherObj);
    } catch (error) {
      console.error(`Failed to fetch voucher UUID: ${error}`);
    }
  };

  useMemo(async() => {
    if(dashboard && servers){
      if(dashboard.widgets.find(widget => widget.widget === 'ListSelectWidget')){
        await fetchVoucherUuid().then(() => setMappingData(false));
        setUsingVoucher(true);
      }

      setVariables(dashboard.variables.map(variable => {
        if(variable.name === 'date_start' || variable.name === 'date_end') setUsingDate(true);
        return fillVariables(variable);
      }));
      setMappingData(false);
    }
  }, [ dashboard, servers ]);

  /*useEffect(() => {
    if(dashboard && servers){
      setVariables(dashboard.variables.map(variable => fillVariables(variable)));
    }
  }, [ voucher ]);*/

  useEffect(() => {
    let canRender = variables && dashboard && servers;

    if(!canRender){
      if(usingVoucher && !voucher.x){
        return;
      }
      else if(usingDate && (!startDate || !endDate)){
        return;
      };
      return;
    }

      const widgets = dashboard.layout.root.children.map(child => {
        if(!child.children){
          return {
            ...child,
            ...createWidget(child.widget, servers, dashboard, variables)[0],
          };
        }
        return {
          ...child,
          children: child.children.map(c => {
            return {
              ...c,
              ...createWidget(c.widget, servers, dashboard, variables)[0],
            };
          }),
        };
      });
      setWidgets(widgets);
      setFirstRender(false);
  }, [ variables ]);

  return(
    <Layout>
      {
        widgets && !mappingData ?
          <>
            <div className='story-header d-flex justify-content-between py-3 px-2'>
              <h2>{ dashboard.title ?? '' }</h2>
            </div>
            <div className='story-grid' style={ gridStyle }>
              {
                widgets.map((widget, i) => {
                  if(widget.children){
                    return(
                      <div key={i} style={{
                        flexBasis: '100%',
                        display: 'flex',
                        flexWrap: 'wrap',
                        flexDirection: 'row',
                        justifyContent: 'space-evenly',
                      }}>
                        {
                          widget.children.map(w => {
                            return(
                              <StoryItem
                                key={w.name}
                                voucher={isVoucherRelated(w, voucher)}
                                changeVoucher={setVoucher}
                                dashboard={dashboard}
                                servers={servers}
                                variables={variables}
                                widget={w}
                                parent={widget}
                                startDate={isDateRelated(w, startDate, endDate)?.startDate}
                                endDate={isDateRelated(w, startDate, endDate)?.endDate}
                                changeStartDate={setStartDate}
                                changeEndDate={setEndDate}
                              />
                            );
                          })
                        }
                      </div>
                    );
                  }

                  return <StoryItem
                            key={widget.name}
                            voucher={isVoucherRelated(widget, voucher)}
                            changeVoucher={setVoucher}
                            dashboard={dashboard}
                            servers={servers}
                            variables={variables}
                            widget={widget}
                            parent={null}
                            startDate={isDateRelated(widget, startDate, endDate)?.startDate}
                            endDate={isDateRelated(widget, startDate, endDate)?.endDate}
                            changeStartDate={setStartDate}
                            changeEndDate={setEndDate}
                            />;
                })
              }
            </div>
          </> : <></>
      }
    </Layout>
  );
};

export default General;